import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Textarea } from '../ui/textarea';
import { ScrollArea } from '../ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../ui/sheet';
import { Shield, Info, Key, Loader2, Lock } from 'lucide-react';

export const RoleFormPanel = ({ 
  role, 
  permissions = [],
  isOpen, 
  onClose, 
  onSubmit 
}) => {
  const { t } = useTranslation('security');
  const [activeTab, setActiveTab] = useState('basic');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permission_ids: []
  });

  // Group permissions by module
  const groupedPermissions = permissions.reduce((acc, perm) => {
    if (!acc[perm.module]) acc[perm.module] = [];
    acc[perm.module].push(perm);
    return acc;
  }, {});

  const moduleLabels = {
    patients: t('rbac.modules.patients'),
    orders: t('rbac.modules.orders'),
    clinical: t('rbac.modules.clinical'),
    medications: t('rbac.modules.medications'),
    specialists: t('rbac.modules.specialists'),
    reports: t('rbac.modules.reports'),
    config: t('rbac.modules.config'),
    users: t('rbac.modules.users'),
    roles: t('rbac.modules.roles'),
    audit: t('rbac.modules.audit'),
    whatsapp: t('rbac.modules.whatsapp'),
    geolocation: t('rbac.modules.geolocation'),
    ocr: t('rbac.modules.ocr')
  };

  useEffect(() => {
    if (isOpen) {
      if (role) {
        setFormData({
          name: role.name || '',
          description: role.description || '',
          permission_ids: role.permission_ids || role.permissions?.map(p => p.id) || []
        });
      } else {
        setFormData({
          name: '',
          description: '',
          permission_ids: []
        });
      }
      setActiveTab('basic');
    }
  }, [role, isOpen]);

  const handlePermissionToggle = (permId) => {
    const currentPerms = formData.permission_ids || [];
    if (currentPerms.includes(permId)) {
      setFormData({...formData, permission_ids: currentPerms.filter(id => id !== permId)});
    } else {
      setFormData({...formData, permission_ids: [...currentPerms, permId]});
    }
  };

  const handleModuleToggle = (module) => {
    const modulePermIds = groupedPermissions[module].map(p => p.id);
    const currentPerms = formData.permission_ids || [];
    const allSelected = modulePermIds.every(id => currentPerms.includes(id));
    
    if (allSelected) {
      setFormData({
        ...formData, 
        permission_ids: currentPerms.filter(id => !modulePermIds.includes(id))
      });
    } else {
      setFormData({
        ...formData, 
        permission_ids: [...new Set([...currentPerms, ...modulePermIds])]
      });
    }
  };

  const handleSubmit = async () => {
    if (!formData.name) return;
    
    setLoading(true);
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error('Error saving role:', error);
    }
    setLoading(false);
  };

  const selectedCount = formData.permission_ids?.length || 0;

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-2xl overflow-hidden flex flex-col p-0">
        <SheetHeader className="px-6 py-4 border-b bg-gradient-to-r from-[#E63946]/5 to-transparent">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-[#E63946]/10 flex items-center justify-center">
              <Shield className="h-6 w-6 text-[#E63946]" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <SheetTitle className="text-lg font-semibold">
                  {role?.id ? t('rbac.roleForm.editTitle') : t('rbac.roleForm.title')}
                </SheetTitle>
                {role?.is_system && (
                  <Badge variant="secondary" className="text-xs">
                    <Lock className="h-3 w-3 mr-1" />
                    {t('rbac.systemRole')}
                  </Badge>
                )}
              </div>
              <SheetDescription>
                {role?.id ? t('rbac.roleForm.editSubtitle') : t('rbac.roleForm.subtitle')}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="px-6 py-2 border-b justify-start rounded-none bg-transparent">
            <TabsTrigger value="basic" className="data-[state=active]:bg-[#E63946]/10 data-[state=active]:text-[#E63946]">
              <Info className="h-4 w-4 mr-1" />
              {t('rbac.roleForm.tabBasic')}
            </TabsTrigger>
            <TabsTrigger value="permissions" className="data-[state=active]:bg-[#E63946]/10 data-[state=active]:text-[#E63946]">
              <Key className="h-4 w-4 mr-1" />
              {t('rbac.roleForm.tabPermissions')}
              {selectedCount > 0 && (
                <Badge variant="secondary" className="ml-2 text-xs">
                  {selectedCount}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <ScrollArea className="flex-1">
            <TabsContent value="basic" className="p-6 space-y-4 mt-0">
              <div className="space-y-2">
                <Label>{t('rbac.roleForm.name')} *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder={t('rbac.roleForm.namePlaceholder')}
                  disabled={role?.is_system}
                  data-testid="role-name"
                />
              </div>

              <div className="space-y-2">
                <Label>{t('rbac.roleForm.description')}</Label>
                <Textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder={t('rbac.roleForm.descriptionPlaceholder')}
                  rows={3}
                  data-testid="role-description"
                />
              </div>
            </TabsContent>

            <TabsContent value="permissions" className="p-6 space-y-4 mt-0">
              <div className="space-y-4">
                {Object.entries(groupedPermissions).map(([module, perms]) => {
                  const allSelected = perms.every(p => (formData.permission_ids || []).includes(p.id));
                  const someSelected = perms.some(p => (formData.permission_ids || []).includes(p.id));
                  
                  return (
                    <div key={module} className="border rounded-lg overflow-hidden">
                      <div 
                        className="flex items-center space-x-3 p-3 bg-muted/50 cursor-pointer hover:bg-muted/70 transition-colors"
                        onClick={() => handleModuleToggle(module)}
                      >
                        <Checkbox
                          id={`module-${module}`}
                          checked={allSelected}
                          className={someSelected && !allSelected ? 'data-[state=checked]:bg-gray-400' : ''}
                          onCheckedChange={() => handleModuleToggle(module)}
                        />
                        <label
                          htmlFor={`module-${module}`}
                          className="text-sm font-semibold cursor-pointer flex-1"
                        >
                          {moduleLabels[module] || module}
                        </label>
                        <Badge variant="outline" className="text-xs">
                          {perms.filter(p => (formData.permission_ids || []).includes(p.id)).length}/{perms.length}
                        </Badge>
                      </div>
                      <div className="p-3 grid grid-cols-2 gap-2 border-t">
                        {perms.map((perm) => (
                          <div 
                            key={perm.id} 
                            className={`flex items-center space-x-2 p-2 rounded cursor-pointer transition-colors ${
                              (formData.permission_ids || []).includes(perm.id)
                                ? 'bg-[#E63946]/5'
                                : 'hover:bg-muted/50'
                            }`}
                            onClick={() => handlePermissionToggle(perm.id)}
                          >
                            <Checkbox
                              id={perm.id}
                              checked={(formData.permission_ids || []).includes(perm.id)}
                              onCheckedChange={() => handlePermissionToggle(perm.id)}
                            />
                            <label
                              htmlFor={perm.id}
                              className="text-sm cursor-pointer"
                            >
                              {perm.name}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </TabsContent>
          </ScrollArea>
        </Tabs>

        <div className="px-6 py-4 border-t bg-muted/30 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            {t('common:cancel')}
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading || !formData.name || role?.is_system} 
            className="bg-[#E63946] hover:bg-[#d32836]"
            data-testid="save-role-btn"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {role?.id ? t('rbac.roleForm.update') : t('rbac.roleForm.save')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default RoleFormPanel;
